/**
 * Notes for the chosen correction and the eye (SPEC «Notes box» + expert feedback). Educational, never orders.
 * Warnings (amber) flag a mismatch between the eye and the correction; notes explain a condition or an effect.
 */

import { baseCorrection, isIol, isToric, type CorrectionChoice, type Profile } from './profile';

export type WarningKey =
  | 'w.kc_lasik' | 'w.high_lasik' | 'w.lasik_near' | 'w.aphakia_lasik' | 'w.cat_icl' | 'w.cat_glasses'
  | 'w.retina_multi' | 'w.astig_nontoric' | 'w.retina_any' | 'w.clear_lens_young' | 'w.amblyopia';

export type NoteKey = 'n.aphakia' | 'n.rp_dark' | 'n.hr' | 'n.dysphotopsia';

/** Spherical equivalent at or beyond which laser correction is usually not offered, D (DECISIONS D-009). */
export const LASIK_MYOPIA_LIMIT = 8;

export function warningsFor(p: Profile, choice: CorrectionChoice): WarningKey[] {
  const base = baseCorrection(choice.id);
  const toric = choice.toric || isToric(choice.id);
  const iol = isIol(choice.id);
  const lens = iol || base === 'icl';
  const se = p.sph + Math.min(0, p.cyl) / 2;
  const retinal = p.glaucoma > 0 || p.amd > 0 || p.dr > 0 || p.hr > 0 || p.rp > 0;
  const out: WarningKey[] = [];

  if (base === 'lasik' && p.keratoconus > 0) out.push('w.kc_lasik');
  if (base === 'lasik' && p.aphakia) out.push('w.aphakia_lasik');
  if (base === 'lasik' && !p.aphakia && se <= -LASIK_MYOPIA_LIMIT) out.push('w.high_lasik');
  if (base === 'lasik' && !p.aphakia && se < -0.25) out.push('w.lasik_near');
  if (base === 'icl' && p.cataract > 0) out.push('w.cat_icl');
  if (p.cataract > 0 && !p.aphakia && (base === 'glasses' || base === 'progressive' || base === 'lasik' || base === 'rgp')) {
    out.push('w.cat_glasses');
  }
  if ((base === 'iol_tri' || base === 'iol_edof') && (p.amd > 0 || p.glaucoma >= 2 || p.dr > 0 || p.rp >= 2 || p.hr >= 3)) {
    out.push('w.retina_multi');
  }
  if (p.cyl <= -1 && lens && !toric) out.push('w.astig_nontoric');
  if (retinal && base !== 'none') out.push('w.retina_any');
  if (iol && p.age < 40 && p.cataract === 0 && !p.aphakia) out.push('w.clear_lens_young');
  if (p.amblyopia > 0 && base !== 'none') out.push('w.amblyopia');
  return out;
}

export function notesFor(p: Profile, choice: CorrectionChoice): NoteKey[] {
  const base = baseCorrection(choice.id);
  const out: NoteKey[] = [];
  if (p.aphakia) out.push('n.aphakia');
  if (p.rp > 0) out.push('n.rp_dark');
  if (p.hr > 0) out.push('n.hr');
  if (base === 'iol_tri' || base === 'iol_edof' || base === 'lasik' || base === 'icl') out.push('n.dysphotopsia');
  return out;
}
